(function (scope) {
  'use strict';

  function optionNames(product) {
    return product.options.map((option) => typeof option === 'string' ? option : option.name);
  }

  function variantOptions(variant) {
    return variant.options || [variant.option1, variant.option2, variant.option3].filter((value) => value != null);
  }

  function selectionFor(product, seedId) {
    const names = optionNames(product);
    const seed = product.variants.find((variant) => String(variant.id) === String(seedId)) || product.variants[0];
    const sizeIndex = names.findIndex((name) => /size/i.test(name));
    const selected = names.map((name, index) => sizeIndex >= 0 && index !== sizeIndex ? variantOptions(seed)[index] : null);
    if (product.variants.length === 1 && names[0] === 'Title' && variantOptions(seed)[0] === 'Default Title') selected[0] = 'Default Title';
    return { names, selected, sizeIndex };
  }

  function matchingVariant(product, selected) {
    if (selected.some((value) => value == null)) return null;
    return product.variants.find((variant) => variantOptions(variant).every((value, index) => value === selected[index])) || null;
  }

  function optionAvailable(product, selected, index, value) {
    return product.variants.some((variant) => variant.available && variantOptions(variant).every((candidate, position) => {
      if (position === index) return candidate === value;
      return selected[position] == null || candidate === selected[position];
    }));
  }

  const model = { optionNames, variantOptions, selectionFor, matchingVariant, optionAvailable };
  if (typeof module !== 'undefined' && module.exports) module.exports = model;
  if (!scope.document) return;
  scope.ChuppsShopping = model;

  const dialog = document.querySelector('#chupps-size-dialog');
  if (!dialog || !dialog.showModal) return;
  const form = dialog.querySelector('[data-quick-buy-form]');
  const submit = dialog.querySelector('[data-buy-submit]');
  const message = dialog.querySelector('[data-buy-message]');
  const options = dialog.querySelector('[data-buy-options]');
  const cache = new Map();
  let current = null;
  let opener = null;
  let loading = false;
  let request = 0;

  function swatchesFromCard(card) {
    const swatches = new Map();
    card?.querySelectorAll('.radio__button label[data-label]').forEach((label) => {
      const value = label.querySelector('.visually-hidden')?.textContent.trim();
      // Read the authored swatch image. During the theme's background-image
      // transition, computed style can be a temporary cross-fade() value that
      // cannot be reused as the popup button's background image.
      const image = label.style.getPropertyValue('--bg-image') || label.style.backgroundImage || getComputedStyle(label).backgroundImage;
      if (value && image && image !== 'none' && !/\bcross-fade\(/i.test(image)) swatches.set(value.toLocaleLowerCase(), image);
    });
    return swatches;
  }

  function productEndpoint(link) {
    const card = link.closest('product-grid-item');
    const visible = card?.querySelector('product-grid-item-variant:not([hidden])');
    const imageLink = visible?.querySelector('[data-grid-link]');
    const url = new URL(imageLink?.getAttribute('href') || link.href, location.origin);
    return url.pathname.replace(/\/$/, '') + '.js';
  }

  function loadProduct(endpoint) {
    if (!cache.has(endpoint)) {
      const productRequest = fetch(endpoint, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error(dialog.dataset.errorLabel);
          return response.json();
        })
        .catch((error) => {
          cache.delete(endpoint);
          throw error;
        });
      cache.set(endpoint, productRequest);
    }
    return cache.get(endpoint);
  }

  function prefetchProduct(link) {
    loadProduct(productEndpoint(link)).catch(() => {});
  }

  function renderLoadingOptions(card) {
    const skeleton = document.createElement('div');
    skeleton.className = 'chupps-buy-skeleton';
    skeleton.setAttribute('aria-hidden', 'true');

    const addGroup = (kind, count) => {
      const group = document.createElement('div');
      group.className = `chupps-buy-skeleton__group chupps-buy-skeleton__group--${kind}`;
      const label = document.createElement('span');
      label.className = 'chupps-buy-skeleton__label';
      const values = document.createElement('div');
      values.className = 'chupps-buy-skeleton__values';
      for (let index = 0; index < count; index += 1) {
        const choice = document.createElement('span');
        choice.className = 'chupps-buy-skeleton__choice';
        values.append(choice);
      }
      group.append(label, values);
      skeleton.append(group);
    };

    const colorCount = card?.querySelectorAll('.radio__button label[data-label]').length || 3;
    addGroup('color', Math.min(colorCount, 5));
    addGroup('size', 5);
    options.replaceChildren(skeleton);
    options.setAttribute('aria-busy', 'true');
  }

  function setMessage(text, error = false) {
    message.textContent = text;
    message.setAttribute('role', error ? 'alert' : 'status');
  }

  function closeDialog() {
    if (loading) return;
    request += 1;
    dialog.close();
  }

  function renderOptions() {
    const { product, selected, names, sizeIndex, colorIndex } = current;
    const focused = document.activeElement;
    const focusIndex = focused?.dataset.optionIndex;
    const focusValue = focused?.dataset.optionValue;
    options.removeAttribute('aria-busy');
    options.replaceChildren();
    names.forEach((name, index) => {
      if (sizeIndex >= 0 && index !== sizeIndex && index !== colorIndex) return;
      if (name === 'Title' && product.variants.length === 1 && selected[index] === 'Default Title') return;
      const fieldset = document.createElement('fieldset');
      fieldset.className = `chupps-buy-option${index === colorIndex ? ' chupps-buy-option--color' : ''}`;
      const legend = document.createElement('legend');
      legend.textContent = name;
      const values = document.createElement('div');
      values.className = 'chupps-buy-option__values';
      [...new Set(product.variants.map((variant) => variantOptions(variant)[index]))].forEach((value) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.textContent = value;
        button.setAttribute('aria-label', value);
        button.title = value;
        button.dataset.optionIndex = index;
        button.dataset.optionValue = value;
        button.setAttribute('aria-pressed', String(selected[index] === value));
        if (index === colorIndex) {
          const variant = product.variants.find((candidate) => variantOptions(candidate)[index] === value);
          const image = current.colorSwatches.get(String(value).toLocaleLowerCase()) || (variant?.featured_image?.src ? `url("${variant.featured_image.src}")` : '');
          if (image) button.style.setProperty('--chupps-color-swatch', image);
        }
        const availabilitySelection = index === colorIndex && sizeIndex >= 0
          ? selected.map((selectedValue, position) => position === sizeIndex ? null : selectedValue)
          : selected;
        button.disabled = !optionAvailable(product, availabilitySelection, index, value);
        if (button.disabled) button.title = dialog.dataset.soldOutLabel;
        values.append(button);
      });
      fieldset.append(legend, values);
      options.append(fieldset);
    });
    const variant = matchingVariant(product, selected);
    submit.disabled = loading || !variant?.available;
    submit.textContent = variant && !variant.available ? dialog.dataset.soldOutLabel : dialog.dataset.addLabel;
    if (focusIndex != null) {
      [...options.querySelectorAll('button')].find((button) => button.dataset.optionIndex === focusIndex && button.dataset.optionValue === focusValue)?.focus();
    }
  }

  document.addEventListener('pointerover', (event) => {
    const link = event.target.closest('[data-quick-buy]');
    if (!link || event.pointerType === 'touch' || link.contains(event.relatedTarget)) return;
    prefetchProduct(link);
  }, true);
  document.addEventListener('focusin', (event) => {
    const link = event.target.closest('[data-quick-buy]');
    if (link) prefetchProduct(link);
  }, true);

  document.addEventListener('click', async (event) => {
    const link = event.target.closest('[data-quick-buy]');
    if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const card = link.closest('product-grid-item');
    const visible = card?.querySelector('product-grid-item-variant:not([hidden])');
    const imageLink = visible?.querySelector('[data-grid-link]');
    const url = new URL(imageLink?.getAttribute('href') || link.href, location.origin);
    const endpoint = productEndpoint(link);
    const seedId = visible?.dataset.gridItemVariant || link.closest('chupps-quick-buy').dataset.variantId;
    const token = ++request;
    opener = link;
    current = null;
    loading = false;
    renderLoadingOptions(card);
    submit.disabled = true;
    dialog.querySelector('[data-buy-product]').textContent = card?.getAttribute('aria-label') || '';
    dialog.querySelector('[data-buy-context]').textContent = '';
    dialog.querySelector('[data-buy-details]').href = url.href;
    setMessage(dialog.dataset.optionsLoadingLabel);
    dialog.showModal();
    try {
      const product = await loadProduct(endpoint);
      if (token !== request || !dialog.open) return;
      if (!product.variants?.length || product.variants.length >= 250 || product.requires_selling_plan) {
        options.removeAttribute('aria-busy');
        options.replaceChildren();
        setMessage(dialog.dataset.unavailableLabel);
        return;
      }
      current = { product, ...selectionFor(product, seedId), colorSwatches: swatchesFromCard(card) };
      current.colorIndex = current.names.findIndex((name) => /colou?r/i.test(name));
      dialog.querySelector('[data-buy-product]').textContent = product.title;
      dialog.querySelector('[data-buy-context]').textContent = current.names
        .map((name, index) => current.selected[index] && index !== current.sizeIndex && index !== current.colorIndex && name !== 'Title' ? `${name}: ${current.selected[index]}` : '')
        .filter(Boolean).join(' · ');
      setMessage(current.sizeIndex >= 0 ? dialog.dataset.selectLabel : '');
      renderOptions();
    } catch (error) {
      if (token === request && dialog.open) {
        options.removeAttribute('aria-busy');
        options.replaceChildren();
        setMessage(dialog.dataset.errorLabel, true);
      }
    }
  }, true);

  options.addEventListener('click', (event) => {
    const button = event.target.closest('[data-option-index]');
    if (!button || button.disabled || loading || !current) return;
    const index = Number(button.dataset.optionIndex);
    current.selected[index] = button.dataset.optionValue;
    if (index === current.colorIndex && current.sizeIndex >= 0 && current.selected[current.sizeIndex] != null &&
        !optionAvailable(current.product, current.selected, current.sizeIndex, current.selected[current.sizeIndex])) {
      current.selected[current.sizeIndex] = null;
    }
    setMessage('');
    renderOptions();
  });

  form.addEventListener('submit', async (event) => {
    event.preventDefault();
    if (loading || !current) return;
    const variant = matchingVariant(current.product, current.selected);
    if (!variant?.available) {
      setMessage(dialog.dataset.selectLabel, true);
      return;
    }
    loading = true;
    submit.disabled = true;
    submit.textContent = dialog.dataset.loadingLabel;
    form.setAttribute('aria-busy', 'true');
    options.querySelectorAll('button').forEach((button) => { button.disabled = true; });
    setMessage('');
    let added = false;
    try {
      const root = scope.Shopify?.routes?.root || scope.theme?.routes?.root_url || '/';
      const properties = {};
      if (opener.closest('chupps-quick-buy').dataset.preorderLabel) properties[opener.closest('chupps-quick-buy').dataset.preorderLabel] = opener.closest('chupps-quick-buy').dataset.preorderValue;
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1, properties }] })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : dialog.dataset.errorLabel);
      added = true;
      setMessage(dialog.dataset.addedLabel);
      const cartResponse = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (cartResponse.ok) document.dispatchEvent(new CustomEvent('theme:cart:change', { detail: { cart: await cartResponse.json() }, bubbles: true }));
    } catch (error) {
      setMessage(added ? dialog.dataset.addedLabel : error.message || dialog.dataset.errorLabel, !added);
    } finally {
      loading = false;
      form.removeAttribute('aria-busy');
      if (added) {
        dialog.close();
        document.dispatchEvent(new CustomEvent('theme:cart:reload', { bubbles: true }));
        if (!scope.theme?.state?.cartOpen) document.querySelector('[data-drawer-toggle="drawer-cart"]')?.click();
      } else {
        renderOptions();
      }
    }
  });

  dialog.querySelector('[data-close-size]').addEventListener('click', closeDialog);
  dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeDialog(); });
  dialog.addEventListener('click', (event) => { if (event.target === dialog) closeDialog(); });
  dialog.addEventListener('close', () => { request += 1; current = null; opener?.focus(); });
})(typeof window === 'undefined' ? globalThis : window);
